import globals from "@lvt/globals";
import { createAsyncThunk, SerializedError } from "@reduxjs/toolkit";
import Ajv from "ajv";
import axios from "axios";
import applyCaseMiddleware from "axios-case-converter";

import { DispatchType } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import { UserLists, UserListInput, CamList } from "@/store/userLists/types";


const fetchUserListsDataSchema = {
    type: "object",
    required: ["status", "statusMessage", "data"],
    properties: {
        status: { type: "integer" },
        statusMessage: { type: "string" },
        data: {
            type: "object",
            required: ["favorites"],
            properties: {
                favorites: {
                    type: ["object", "null"],
                    required: ["id", "name", "cameras"],
                    properties: {
                        id: { type: "integer" },
                        name: { type: "string" },
                        cameras: {
                            type: "array",
                            items: {
                                type: "object",
                                required: ["camId", "camName", "clientId", "lastImageUrl"],
                                additionalProperties: false,
                                properties: {
                                    camId: { type: "integer" },
                                    camName: { type: "string" },
                                    clientId: { type: "number" },
                                    lastImageUrl: { type: ["string", "null"] },
                                },
                            },
                        },
                    },
                    additionalProperties: false,
                },
            },
            additionalProperties: true,
        },
    },
    additionalProperties: false,
};

const updateUserListsSchema = {
    type: "object",
    required: ["status", "statusMessage", "data"],
    properties: {
        status: { type: "integer" },
        statusMessage: { type: "string" },
        data: {
            type: ["object", "null"],
            required: ["id", "name", "cameras"],
            properties: {
                id: { type: "integer" },
                name: { type: "string" },
                cameras: {
                    type: "array",
                    items: {
                        type: "object",
                        required: ["camId", "camName", "clientId", "lastImageUrl"],
                        additionalProperties: false,
                        properties: {
                            camId: { type: "integer" },
                            camName: { type: "string" },
                            clientId: { type: "number" },
                            lastImageUrl: { type: ["string", "null"] },
                        },
                    },
                },
            },
            additionalProperties: false,
        },
    },
    additionalProperties: false,
};

const fetchUserListsValidator = new Ajv().compile(fetchUserListsDataSchema);

export const fetchUserListsData = createAsyncThunk("fetchUserListsData", async (userId: number, { dispatch }) => {
    try {
        const request = applyCaseMiddleware(
            axios.create({
                baseURL: globals.userdashboard.proxyUrl,
            }),
        );
        const response = await request.get(`/api/cam_lists/${userId}`, {
            withCredentials: true,
        });

        if (fetchUserListsValidator(response.data)) {
            if (response.data.status == 200) {
                return response.data.data as UserLists;
            } else {
                const error: SerializedError = {
                    name: "statusCode",
                    message: "Error" + response.data.status + ": " + response.data.statusMessage,
                };

                throw error;
            }
        } else {
            const error: SerializedError = {
                name: "validation",
                message: JSON.stringify(fetchUserListsValidator.errors),
            };

            throw error;
        }
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error(error.message);
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: "Failed to retrieve user data",
                }),
            );
        }

        throw error;
    }
});

const updateUserListsValidator = new Ajv().compile(updateUserListsSchema);

export const addUserLists = createAsyncThunk("addUserLists", async (data: UserListInput, { dispatch }) => {
    try {
        const request = applyCaseMiddleware(
            axios.create({
                baseURL: globals.userdashboard.proxyUrl,
            }),
        );
        const response = await request.post("/api/cam_lists", data, {
            withCredentials: true,
        });

        if (updateUserListsValidator(response.data)) {
            if (response.data.status === 200) {
                return response.data.data as CamList;
            } else {
                const error: SerializedError = {
                    name: "statusCode",
                    message: "Error" + response.data.status + ": " + response.data.statusMessage,
                };

                throw error;
            }
        } else {
            const error: SerializedError = {
                name: "validation",
                message: JSON.stringify(updateUserListsValidator.errors),
            };

            throw error;
        }
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error(error.message);
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: "Failed to add favorite list",
                }),
            );
        }

        throw error;
    }
});

export const putUserFavData = createAsyncThunk(
    "putUserFavData",
    async ({ favoriteListId, data }: { favoriteListId: number; data: UserListInput }, { dispatch }) => {
        try {
            const request = applyCaseMiddleware(
                axios.create({
                    baseURL: globals.userdashboard.proxyUrl,
                }),
            );
            const response = await request.put(`/api/cam_lists/${favoriteListId}`, data, {
                withCredentials: true,
            });

            if (updateUserListsValidator(response.data)) {
                if (response.data.status === 200) {
                    return response.data.data as CamList;
                } else {
                    const error: SerializedError = {
                        name: "statusCode",
                        message: "Error" + response.data.status + ": " + response.data.statusMessage,
                    };

                    throw error;
                }
            } else {
                const error: SerializedError = {
                    name: "validation",
                    message: JSON.stringify(updateUserListsValidator.errors),
                };

                throw error;
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                console.error(error.message);
                dispatch(
                    addAlert({
                        severity: Severity.Error,
                        message: "Failed to favorite camera",
                    }),
                );
            }

            throw error;
        }
    },
);

export const updateFavorites = (
    isFavoriteVal: boolean,
    cameraId: number,
    userId: number,
    userLists: UserLists | null,
    dispatch: DispatchType,
) => {
    let newCameras: number[] = [];
    let deleteCameras: number[] = [];

    if (isFavoriteVal) {
        newCameras = [cameraId];
    } else {
        deleteCameras = [cameraId];
    }

    const postObj: UserListInput = {
        userId: userId,
        name: "Favorites",
        isFavorites: true,
        newCameras,
        deleteCameras,
    };

    if (userLists && userLists.favorites && userLists.favorites.id) {
        dispatch(putUserFavData({ favoriteListId: userLists.favorites.id, data: postObj }));
    } else {
        dispatch(addUserLists(postObj));
    }
};
